import React from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  TRIAL_TYPE_OPTIONS,
  ARM_STRUCTURE_OPTIONS,
  ALLOCATION_OPTIONS,
  BLINDING_OPTIONS,
  CENTRE_CONFIGURATION_OPTIONS,
  GEOGRAPHIC_DISTRIBUTION_OPTIONS,
  MASTER_PROTOCOL_FRAMEWORK_OPTIONS,
  ADAPTIVE_FEATURES_OPTIONS,
  PART_PHASE_OPTIONS,
  INTERVENTION_MODEL_OPTIONS,
  ARM_TYPE_OPTIONS,
  emptyCohortRow,
  emptyDirectRow,
  ensurePartSlots,
  syncLegacyFieldsFromTrialSchema,
  MAIN_STUDY_LABEL,
  isMultiPartTrial,
} from '../../constants/trialSchemaConfiguration';

const RadioRow = ({ label, name, options, value, onChange, disabled }) => (
  <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }} disabled={disabled}>
    <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
      {label}
    </FormLabel>
    <RadioGroup row value={value || ''} onChange={(e) => onChange(e.target.value)} name={name}>
      {options.map((opt) => (
        <FormControlLabel
          key={opt.value}
          value={opt.value}
          control={<Radio size="small" />}
          label={opt.label}
          sx={{ mr: 2 }}
        />
      ))}
    </RadioGroup>
  </FormControl>
);

const TrialSchemaConfigurationSection = ({
  values,
  setFieldValue,
  handleBlur,
  isFormDisabled,
  getHoverInfo,
  onLegacySync,
}) => {
  const config = values.study_overview?.trial_schema_configuration || {};
  const framework = config.framework || {};
  const trialTypes = config.trial_types || {};
  const numParts = parseInt(values.study_overview?.number_of_study_parts_new) || 0;
  const parts = config.parts || {};

  const applyConfig = (nextConfig, partsCount = numParts) => {
    const withSlots = ensurePartSlots(nextConfig, partsCount);
    setFieldValue('study_overview.trial_schema_configuration', withSlots);
    const synced = syncLegacyFieldsFromTrialSchema(withSlots, partsCount);
    Object.entries(synced.study_overview).forEach(([k, v]) => {
      if (k !== 'trial_schema_configuration') setFieldValue(`study_overview.${k}`, v);
    });
    Object.entries(synced.study_treatments).forEach(([k, v]) => {
      setFieldValue(`study_treatments.${k}`, v);
    });
    onLegacySync?.(synced);
  };

  const updateFramework = (key, val) => {
    applyConfig({
      ...config,
      framework: { ...framework, [key]: val },
    });
  };

  const updateTrialType = (key, checked) => {
    applyConfig({
      ...config,
      trial_types: { ...trialTypes, [key]: checked },
    });
  };

  const handleNumPartsChange = (n) => {
    const count = parseInt(n) || 0;
    setFieldValue('study_overview.number_of_study_parts_new', count);
    applyConfig(ensurePartSlots(config, count), count);
  };

  const updatePart = (partKey, patch) => {
    const nextParts = {
      ...parts,
      [partKey]: { ...(parts[partKey] || {}), ...patch },
    };
    applyConfig({ ...config, parts: nextParts });
  };

  const updateCountryRow = (index, patch) => {
    const rows = [...(config.country_level || [{ country: '', description: '' }])];
    rows[index] = { ...rows[index], ...patch };
    applyConfig({ ...config, country_level: rows });
  };

  const addCountryRow = () => {
    applyConfig({
      ...config,
      country_level: [...(config.country_level || []), { country: '', description: '' }],
    });
  };

  const removeCountryRow = (index) => {
    const rows = (config.country_level || []).filter((_, i) => i !== index);
    applyConfig({
      ...config,
      country_level: rows.length ? rows : [{ country: '', description: '' }],
    });
  };

  const partKeys = Object.keys(parts)
    .filter((k) => /^part\d+$/i.test(k))
    .sort((a, b) => parseInt(a.replace(/\D/g, ''), 10) - parseInt(b.replace(/\D/g, ''), 10));

  const displayPartKeys = isMultiPartTrial(numParts)
    ? partKeys
    : partKeys.filter((k) => k === 'part1').length > 0
      ? ['part1']
      : [];

  React.useEffect(() => {
    if (!isMultiPartTrial(numParts) && !parts.part1) {
      applyConfig(ensurePartSlots(config, 0), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      {/* Trial Type */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Trial Type
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {TRIAL_TYPE_OPTIONS.map((opt) => (
          <FormControlLabel
            key={opt.key}
            control={
              <Checkbox
                checked={!!trialTypes[opt.key]}
                onChange={(e) => updateTrialType(opt.key, e.target.checked)}
                disabled={isFormDisabled}
                size="small"
              />
            }
            label={opt.label}
          />
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Trial Design / Framework */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Trial Design / Framework
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <RadioRow
            label="Arm Structure"
            name="arm_structure"
            options={ARM_STRUCTURE_OPTIONS}
            value={framework.arm_structure}
            onChange={(v) => updateFramework('arm_structure', v)}
            disabled={isFormDisabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RadioRow
            label="Allocation"
            name="allocation"
            options={ALLOCATION_OPTIONS}
            value={framework.allocation}
            onChange={(v) => updateFramework('allocation', v)}
            disabled={isFormDisabled}
          />
        </Grid>
        <Grid item xs={12}>
          <RadioRow
            label="Blinding"
            name="blinding"
            options={BLINDING_OPTIONS}
            value={framework.blinding}
            onChange={(v) => updateFramework('blinding', v)}
            disabled={isFormDisabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RadioRow
            label="Centre Configuration"
            name="centre_configuration"
            options={CENTRE_CONFIGURATION_OPTIONS}
            value={framework.centre_configuration}
            onChange={(v) => updateFramework('centre_configuration', v)}
            disabled={isFormDisabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RadioRow
            label="Geographic Distribution"
            name="geographic_distribution"
            options={GEOGRAPHIC_DISTRIBUTION_OPTIONS}
            value={framework.geographic_distribution}
            onChange={(v) => updateFramework('geographic_distribution', v)}
            disabled={isFormDisabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Site Planning (Total no. of sites)"
            type="number"
            inputProps={{ min: 0 }}
            value={framework.total_sites ?? ''}
            onChange={(e) => updateFramework('total_sites', e.target.value)}
            onBlur={handleBlur}
            disabled={isFormDisabled}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Participant Planning (Total no. of planned participants)"
            type="number"
            inputProps={{ min: 0 }}
            value={framework.total_planned_participants ?? ''}
            onChange={(e) => updateFramework('total_planned_participants', e.target.value)}
            onBlur={handleBlur}
            disabled={isFormDisabled}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RadioRow
            label="Master Protocol Framework"
            name="master_protocol_framework"
            options={MASTER_PROTOCOL_FRAMEWORK_OPTIONS}
            value={framework.master_protocol_framework}
            onChange={(v) => updateFramework('master_protocol_framework', v)}
            disabled={isFormDisabled}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <RadioRow
            label="Adaptive Features"
            name="adaptive_features"
            options={ADAPTIVE_FEATURES_OPTIONS}
            value={framework.adaptive_features}
            onChange={(v) => updateFramework('adaptive_features', v)}
            disabled={isFormDisabled}
          />
        </Grid>
        {framework.adaptive_features === 'yes' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Brief Description of Adaptation Strategy"
              value={framework.brief_description_of_adaptation_strategy || ''}
              onChange={(e) =>
                updateFramework('brief_description_of_adaptation_strategy', e.target.value)
              }
              onBlur={handleBlur}
              disabled={isFormDisabled}
              size="small"
              multiline
              minRows={3}
              placeholder="Describe the adaptive design elements and adaptation strategy"
            />
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Number of Trial Parts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Number of Trial Parts"
            type="number"
            inputProps={{ min: 0 }}
            value={numParts || ''}
            onChange={(e) => handleNumPartsChange(e.target.value)}
            onBlur={handleBlur}
            disabled={isFormDisabled}
            helperText="Leave 0 for a single Main Study; enter 2+ for Part 1, Part 2, …"
          />
        </Grid>
      </Grid>

      {/* Part Information — per PDF pages 6–7 */}
      {displayPartKeys.map((partKey, partIndex) => {
        const part = parts[partKey] || {};
        const partNum = partIndex + 1;
        const numCohorts = parseInt(part.num_cohorts) || 0;
        const partLabel = isMultiPartTrial(numParts)
          ? part.part_name?.trim() || `Part ${partNum}`
          : part.part_name?.trim() || MAIN_STUDY_LABEL;

        const setCohortRows = (rows) => updatePart(partKey, { cohort_rows: rows });
        const setDirectRows = (rows) => updatePart(partKey, { direct_rows: rows });

        const handleCohortCountChange = (count) => {
          const n = parseInt(count) || 0;
          const existing = part.cohort_rows || [];
          const rows =
            n > existing.length
              ? [...existing, ...Array.from({ length: n - existing.length }, () => emptyCohortRow())]
              : existing.slice(0, n);
          updatePart(partKey, { num_cohorts: n, cohort_rows: rows });
        };

        return (
          <Box
            key={partKey}
            sx={{ mb: 2, p: 2, border: '1px solid #dbe3eb', borderRadius: 2, backgroundColor: '#fafbfc' }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              Part Information — {partLabel}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Part Name"
                  value={part.part_name || ''}
                  onChange={(e) => updatePart(partKey, { part_name: e.target.value })}
                  disabled={isFormDisabled}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Phase"
                  value={part.phase || ''}
                  onChange={(e) => updatePart(partKey, { phase: e.target.value })}
                  disabled={isFormDisabled}
                  size="small"
                >
                  <MenuItem value="">
                    <em>Select phase</em>
                  </MenuItem>
                  {PART_PHASE_OPTIONS.map((ph) => (
                    <MenuItem key={ph} value={ph}>
                      {ph}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <RadioRow
                  label="Intervention Model"
                  name={`intervention_model_${partKey}`}
                  options={INTERVENTION_MODEL_OPTIONS}
                  value={part.intervention_model}
                  onChange={(v) => updatePart(partKey, { intervention_model: v })}
                  disabled={isFormDisabled}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Number of Cohorts"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={part.num_cohorts ?? ''}
                  onChange={(e) => handleCohortCountChange(e.target.value)}
                  disabled={isFormDisabled}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Total no. of Regimens"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={part.num_regimens ?? ''}
                  onChange={(e) => updatePart(partKey, { num_regimens: parseInt(e.target.value) || 0 })}
                  disabled={isFormDisabled}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Total no. of Agents"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={part.num_agents ?? ''}
                  onChange={(e) => updatePart(partKey, { num_agents: parseInt(e.target.value) || 0 })}
                  disabled={isFormDisabled}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Regimen Dose Levels with Frequency"
                  value={part.regimen_dose_levels_frequency || ''}
                  onChange={(e) => updatePart(partKey, { regimen_dose_levels_frequency: e.target.value })}
                  disabled={isFormDisabled}
                  size="small"
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Agent Names"
                  value={part.agent_names || ''}
                  onChange={(e) => updatePart(partKey, { agent_names: e.target.value })}
                  disabled={isFormDisabled}
                  size="small"
                  multiline
                  minRows={2}
                />
              </Grid>
            </Grid>

            {/* Cohort Information */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Cohort Information
            </Typography>

            {numCohorts === 0 ? (
              <>
                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                        <TableCell>Arm Type</TableCell>
                        <TableCell>Regimen</TableCell>
                        <TableCell>Dose Levels with Frequency</TableCell>
                        <TableCell>Agent(s) Names</TableCell>
                        <TableCell>Planned Enrollment</TableCell>
                        <TableCell width={60} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(part.direct_rows || []).map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                          <TableCell>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={row.arm_type || ''}
                              onChange={(e) => {
                                const rows = [...(part.direct_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], arm_type: e.target.value };
                                setDirectRows(rows);
                              }}
                              disabled={isFormDisabled}
                            >
                              <MenuItem value="">
                                <em>Select</em>
                              </MenuItem>
                              {ARM_TYPE_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.regimen || ''}
                              onChange={(e) => {
                                const rows = [...(part.direct_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], regimen: e.target.value };
                                setDirectRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.dose_levels_frequency || ''}
                              onChange={(e) => {
                                const rows = [...(part.direct_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], dose_levels_frequency: e.target.value };
                                setDirectRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.agent_names || ''}
                              onChange={(e) => {
                                const rows = [...(part.direct_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], agent_names: e.target.value };
                                setDirectRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={row.planned_enrollment || ''}
                              onChange={(e) => {
                                const rows = [...(part.direct_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], planned_enrollment: e.target.value };
                                setDirectRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            {!isFormDisabled && (
                              <Button
                                size="small"
                                color="error"
                                onClick={() => setDirectRows((part.direct_rows || []).filter((_, i) => i !== rowIdx))}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {!isFormDisabled && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setDirectRows([...(part.direct_rows || []), emptyDirectRow()])}
                  >
                    Add row (0 cohorts)
                  </Button>
                )}
              </>
            ) : (
              <>
                <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
                        <TableCell>Cohort Name/No</TableCell>
                        <TableCell>Arm Type</TableCell>
                        <TableCell>Regimen</TableCell>
                        <TableCell>Dose Levels with Frequency</TableCell>
                        <TableCell>Agent(s) Names</TableCell>
                        <TableCell>Planned Enrollment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(part.cohort_rows || []).map((row, rowIdx) => (
                        <TableRow key={rowIdx}>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.cohort_name || ''}
                              onChange={(e) => {
                                const rows = [...(part.cohort_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], cohort_name: e.target.value };
                                setCohortRows(rows);
                              }}
                              disabled={isFormDisabled}
                              placeholder={`Cohort ${rowIdx + 1}`}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={row.arm_type || ''}
                              onChange={(e) => {
                                const rows = [...(part.cohort_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], arm_type: e.target.value };
                                setCohortRows(rows);
                              }}
                              disabled={isFormDisabled}
                            >
                              <MenuItem value="">
                                <em>Select</em>
                              </MenuItem>
                              {ARM_TYPE_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.regimen || ''}
                              onChange={(e) => {
                                const rows = [...(part.cohort_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], regimen: e.target.value };
                                setCohortRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.dose_levels_frequency || ''}
                              onChange={(e) => {
                                const rows = [...(part.cohort_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], dose_levels_frequency: e.target.value };
                                setCohortRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={row.agent_names || ''}
                              onChange={(e) => {
                                const rows = [...(part.cohort_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], agent_names: e.target.value };
                                setCohortRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              value={row.planned_enrollment || ''}
                              onChange={(e) => {
                                const rows = [...(part.cohort_rows || [])];
                                rows[rowIdx] = { ...rows[rowIdx], planned_enrollment: e.target.value };
                                setCohortRows(rows);
                              }}
                              disabled={isFormDisabled}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        );
      })}

      {!isMultiPartTrial(numParts) && displayPartKeys.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
          Main Study configuration will appear here. Leave trial parts at 0 for a single-study design, or enter 2 or more for Part 1, Part 2, etc.
        </Typography>
      )}

      {isMultiPartTrial(numParts) && displayPartKeys.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
          Enter the number of trial parts to configure part information and cohort tables.
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Country-Level Information — PDF page 8 */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Country-Level Information
      </Typography>
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f1f5f9' }}>
              <TableCell>Country</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {(config.country_level || []).map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.country || ''}
                    onChange={(e) => updateCountryRow(idx, { country: e.target.value })}
                    disabled={isFormDisabled}
                    placeholder="Country name"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.description || ''}
                    onChange={(e) => updateCountryRow(idx, { description: e.target.value })}
                    disabled={isFormDisabled}
                    multiline
                    minRows={1}
                  />
                </TableCell>
                <TableCell>
                  {!isFormDisabled && (config.country_level || []).length > 1 && (
                    <Button size="small" color="error" onClick={() => removeCountryRow(idx)}>
                      <DeleteIcon fontSize="small" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!isFormDisabled && (
        <Button size="small" startIcon={<AddIcon />} onClick={addCountryRow} sx={{ mb: 2 }}>
          Add country
        </Button>
      )}
    </Box>
  );
};

export default TrialSchemaConfigurationSection;
